import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, CTABand } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroAbout from "@/assets/hero/hero-about.jpg.asset.json";
import { SITE } from "@/data/site";
import { BrandNetworkGrid } from "@/components/site/BrandNetwork";

const HERO = {
  src: heroAbout.url,
  alt: "Happy family standing together on the front walkway of a warmly lit suburban rental home at dusk — investor-first mortgage brokerage for real estate investors",
};

const URL = "/about";
const KEYWORDS = [
  "DSCR lender about us",
  "business purpose mortgage broker",
  "investor mortgage company",
  "Florida Colorado DSCR lender",
  "in-house loan underwriting",
  "investor loan officer",
  "LLC vesting lender",
  "real estate investor lending team",
  "Section 8 rental lender",
  "foreign national loan lender",
  "fix and flip lender company",
  "construction loan lender",
].join(", ");

export const Route = createFileRoute("/about")({
  head: () =>
    buildHead({
      title: "About Simply Approved Mortgages | DSCR Investment Lending",
      description:
        "Licensed investor mortgage broker (NMLS #2620881) in Florida and Colorado. DSCR, Foreign National, Construction, Fix & Flip, STR, and Section 8 programs. LLC vesting, no tax returns.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "About", url: URL },
      ],
      service: { name: "Business-Purpose Investment Property Lending" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: About,
});

function About() {
  return (
    <SiteShell>
      <PageHero eyebrow="About" title="Investor-first mortgage brokerage for business-purpose real estate." image={HERO} subtitle="Simply Approved Mortgages LLC (NMLS #2620881) is a licensed mortgage brokerage focused on business-purpose real estate investors — DSCR, Foreign National, Construction, Fix & Flip, Airbnb / STR, and Section 8 scenarios. Business-purpose investment property financing is arranged across our approved-state footprint; consumer / owner-occupied residential brokerage is limited to Florida and Colorado." />
      <PageEssentials />
      <Section>
        <SectionHeader eyebrow="Our story" title="One desk. Every business-purpose program." intro="We built the Simply Approved DSCR desk to give real estate investors a single point of contact across the deals they actually do — long-term rentals, foreign-national purchases, ground-up builds, flips, short-term rentals, and Section 8 portfolios." />
        <blockquote className="mt-8 border-l-4 border-brand-gold pl-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Our mission</div>
          <p className="mt-1 font-display text-2xl italic text-foreground md:text-3xl">{SITE.mission}.</p>
        </blockquote>
        <div className="prose prose-slate mt-8 max-w-none">
          <p>
            We are a mortgage brokerage, not a lender. Your scenario is packaged and placed with
            wholesale lending partners whose guidelines fit the deal; those lenders underwrite,
            approve, and fund the loan. Our job is structuring the file, shopping it, and keeping
            the transaction moving to the closing table.
          </p>
          <h3>What sets us apart</h3>
          <ul>
            <li><strong>Program breadth.</strong> Six investor programs — one team.</li>
            <li><strong>Clear communication.</strong> Initial scenario review on complete submissions — usually the same business day. Formal pricing and terms follow lender underwriting.</li>
            <li><strong>Entity-friendly.</strong> LLC, LP, S-Corp, C-Corp, and Series LLC vesting are commonly accepted on business-purpose programs.</li>
            <li><strong>Licensing.</strong> Simply Approved Mortgages LLC, NMLS #2620881 — Florida Mortgage Broker License #MBR7685 and Colorado mortgage company registration for consumer residential activity; business-purpose investment property financing arranged in additional approved jurisdictions.</li>
          </ul>
        </div>
      </Section>

      <Section id="network">
        <SectionHeader
          eyebrow="Our network"
          title="The Simply Approved family of sites"
          intro="Investors' needs often go beyond one rental loan. These sister sites, all part of Simply Approved Mortgages (NMLS #2620881), cover home, business and equity financing."
        />
        <BrandNetworkGrid />
      </Section>


      <CTABand title="Talk to an investor loan officer" body="Send us your scenario. Real answers, real pricing." />
    <PageOutro />
      </SiteShell>
  );
}
